// tslint:disable:no-string-literal

import {formatCurrency, formatNumber} from '@angular/common';
import {
    BodyScrollEvent,
    CellClassParams,
    CellClickedEvent,
    ColDef,
    ColGroupDef, ColumnMovedEvent, ColumnResizedEvent, DragStoppedEvent,
    GetContextMenuItemsParams,
    GridOptions,
    ValueFormatterParams,
    ValueGetterParams
} from 'ag-grid-community';
import {
    centeredColumnDef,
    defaultLoadingOverlayTemplate,
    defaultPriceCellFormatter,
    defaultPriceCellFormatterWithDollarSign
} from '../ag-grid-contrib';
import {OptionType} from '../options-common/options.model';
import {isNullOrUndefined, isValidNumber, isVoid} from '../utils';
import {OptionsPricingGridComponentBase} from './options-pricing-grid-base.component';
import {PricingGridStrategy} from './model/pricing-grid.strategy';
import {
    CopyToTOSDesktopDestinationId,
} from "../access-control-service.class";
import {PricingGridStrategyColumn} from "./model/pricing-grid-strategy.column";
import {PricingGridRow} from "./model/pricing-grid.row";
import * as Enumerable from "linq";
import {
    OpgPriceColumnCellRendererComponent
} from "./price-column-cell-renderer/opg-price-column-cell-renderer.component";
import {GridSettingsProvider} from "./model/grid-settings.provider";
import {ExpectedMoveArgs} from "../expected-move/expected-move.service";

const minCellWidth = 90;

const maxCellWidth = 120;

export function getOptionsPricingGridModel(this: OptionsPricingGridComponentBase): GridOptions {
    const options: GridOptions = {};

    options.rowData = [];
    options.defaultColDef = centeredColumnDef;
    options.columnDefs = getEmptyGridTemplate(this);
    options.overlayLoadingTemplate = defaultLoadingOverlayTemplate;
    options.rowClass = 'ets-text-centered';
    options.rowSelection = null;
    options.suppressRowClickSelection = true;
    options.rowModelType = 'clientSide';
    options.immutableData = true;
    options.enableBrowserTooltips = true;
    options.suppressColumnVirtualisation = false;

    options.frameworkComponents = {
        'opgPriceCellRenderer': OpgPriceColumnCellRendererComponent
    }

    options.getContextMenuItems = (params: GetContextMenuItemsParams) => {

        let isPricing = false;
        let isOption = false;
        let is3col = false;
        let callOrPut = undefined;

        if (params.column) {
            const field = params.column.getColDef().field;
            isPricing = field.indexOf('pricing-') >= 0;
            isOption = field === 'callPrice' || field === 'putPrice';

            if (isPricing) {
                callOrPut = field.indexOf('-call') > 0 ? 'calls' : 'puts';
            }

            if (this.mode === 'pmd') {
                const col = params.column.getColId();
                is3col = col.startsWith('trans') || col.startsWith('own') || col.startsWith('out');
            }
        }

        const ctxMenu = [
            {
                name: 'Size Columns To Fit',
                disabled: false,
                action: () => params.api.sizeColumnsToFit()
            },
            'autoSizeAll',
        ];

        ctxMenu.push('separator');

        // Repairs a broken layout without touching data. By analogy with the Hedge
        // Matrix's 'Rebuild Grid', but layout-only: strategies are kept (owner
        // ruling 2026-09-01).
        ctxMenu.push({
            name: 'Rebuild Grid',
            disabled: false,
            action: () => this.rebuildGrid()
        });

        ctxMenu.push('separator');

        if (this.mode === 'opg') {

            if (this.bothSidesHasSelected()) {
                ctxMenu.push({
                    name: 'Copy To Other Panel',
                    subMenu: [
                        {
                            name: 'Selected Only',
                            action: () => this.copyOrders('other-panel', params, callOrPut)
                        },
                        {
                            name: 'All',
                            action: () => this.copyOrders('other-panel', params, 'both')
                        }
                    ],
                    disabled: !isPricing && !isOption,
                } as any);

            } else {
                ctxMenu.push({
                    name: 'Copy To Other Panel',
                    action: () => this.copyOrders('other-panel', params, callOrPut),
                    disabled: !isPricing && !isOption,
                } as any);
            }

            if (this.accessControlService.isSecureElementAvailable(CopyToTOSDesktopDestinationId)) {

                ctxMenu.push({
                    name: 'Copy To TOS',
                    disabled: !isPricing && !isOption,
                    action: () => this.copyOrders('tos', params, callOrPut)
                });

            }

        } else {
            if (!is3col && !isOption) {

                ctxMenu.push({
                    name: 'Copy To Other Panel',
                    disabled: !isPricing && !isOption,
                    action: () => this.copyOrders('other-panel', params, callOrPut)
                });

                if (this.accessControlService.isSecureElementAvailable(CopyToTOSDesktopDestinationId)) {
                    ctxMenu.push({
                        name: 'Copy To TOS',
                        disabled: !isPricing && !isOption,
                        action: () => this.copyOrders('tos', params, callOrPut)
                    });
                }
            }
        }

        return ctxMenu;

    };
    options.onGridReady = (args) => this.onGridReady(args);
    options.getRowNodeId = (row) => row.rowStrike;
    options.onCellClicked = (args: CellClickedEvent) => this.onCellClicked(args);

    // options.onBodyScroll = (args: BodyScrollEvent) => this.onBodyScrollEvent(args);

    let lastPricingColumnMoved: ColumnMovedEvent;

    options.onDragStopped = (event: DragStoppedEvent) => {

        if (isVoid(lastPricingColumnMoved)) {
            return;
        }

        const colId = lastPricingColumnMoved.column.getColId();

        if (!colId.startsWith('pricing')) {
            lastPricingColumnMoved = null;
            return;
        }

        const parts = colId.split('-');
        let strategyId = parts[1];
        const optionType = parts[parts.length - 1];

        if (parts.length > 3) {
            strategyId = Enumerable.from(parts).skip(1)
                .take(parts.length - 2)
                .toJoinedString('-');
        }

        let container: PricingGridStrategy[] = [];
        let targetSide: 'calls' | 'puts';
        if (optionType === 'call') {
            container = this.callStrategies;
            targetSide = 'calls';
        } else if (optionType === 'put') {
            container = this.putStrategies;
            targetSide = 'puts';
        }

        const ix = container.findIndex(x => x.strategyId === strategyId);
        if (ix < 0) {
            this.toastr.warning('Strategy column was not identified. Changes in columns order will not be saved');
            return;
        }

        const colGroup: ColGroupDef = lastPricingColumnMoved.api.getColumnDefs().find((x: ColGroupDef) => x.groupId === targetSide) as any;
        const strategyColumns = colGroup.children.filter((x: ColDef) => x.colId.startsWith('pricing-'));
        const column = strategyColumns.find((x: ColDef) => x.colId === colId) as ColDef;
        const indexInGrid = strategyColumns.indexOf(column);

        this.isLoading = true;

        let hadErrors = false;

        setTimeout(() => {
            try {

                let errorMsg = 'Column moved outside of pricing columns range. Changes in columns order won\'t  be saved';

                if (indexInGrid < 0) {
                    this.toastr.warning(errorMsg);
                    return;
                }

                if (indexInGrid > container.length) {
                    this.toastr.warning(errorMsg);
                    return;
                }

                const currentIndexInContainer = container.findIndex((x) => x.strategyId === strategyId);

                const targetIndexInContainer = optionType === 'call' ? container.length - indexInGrid - 1 : indexInGrid;

                const deleted = container.splice(currentIndexInContainer, 1);

                container.splice(targetIndexInContainer, 0, deleted[0]);

            } catch {
                hadErrors = true;
            } finally {
                if (!hadErrors) {
                    if (this.canSaveTemplate(targetSide)) {
                        this.saveTemplate(targetSide)
                            .then(() => {
                            })
                            .catch(() => {
                            });
                    }
                }
                this.isLoading = false;
                lastPricingColumnMoved = null;
            }
        }, 1000);
    };

    options.onColumnResized = (args: ColumnResizedEvent) => {

        // Only a real user drag of the column divider is remembered. Engine-driven
        // resizes (autoSizeColumns / sizeColumnsToFit / api) must NOT be cached --
        // caching those pinned put columns to the bogus width they measured while
        // scrolled out of the viewport, and auto-size could never run on them again.
        if (args.source !== 'uiColumnDragged') {
            return;
        }

        if (!args.finished) {
            return;
        }

        const resized = args.columns || (args.column ? [args.column] : []);

        resized
            .filter(col => col.getColId().startsWith('pricing-'))
            .forEach(col => this.rememberUserColumnWidth(col.getColId(), col.getActualWidth()));
    };

    options.onColumnMoved = (args: ColumnMovedEvent) => {

        lastPricingColumnMoved = args;

        return; // need to wait till drag is finished to handle event
    }

    return options;
}

export function getGridColumnDefinitions(comp: OptionsPricingGridComponentBase) {

    let colDefs = [];

    colDefs = getFullGridTemplate(comp);

    return colDefs;
}

function getFullGridTemplate(comp: OptionsPricingGridComponentBase): ColDef[] {
    const existingDefs = getEmptyGridTemplate(comp);

    let existingCallChildren = [];

    let existingPutChildren = [];

    const existingCallDefs = existingDefs.find((x: any) => x.groupId === 'calls') as ColGroupDef
        || {children: []};

    const existingPutDefs = existingDefs.find((x: any) => x.groupId === 'puts') as ColGroupDef
        || {children: []};

    existingCallChildren = existingCallDefs.children.map((x: ColDef) => {
        return {
            headerName: x.headerName,
            headerClass: x.headerClass,
            headerValueGetter: x.headerValueGetter,
            colId: x.colId,
            field: x.field,
            valueFormatter: x.valueFormatter,
            sortable: x.sortable,
            tooltipField: x.tooltipField,
            hide: x.hide,
            cellStyle: x.cellStyle,
            maxWidth: x.maxWidth,
            minWidth: x.minWidth,
            width: x.width,
            cellRendererParams: x.cellRendererParams,
            cellRenderer: x.cellRenderer
        } as ColDef;
    });

    existingPutChildren = existingPutDefs.children.map((x: ColDef) => {
        return {
            headerName: x.headerName,
            headerClass: x.headerClass,
            headerValueGetter: x.headerValueGetter,
            colId: x.colId,
            field: x.field,
            valueFormatter: x.valueFormatter,
            sortable: x.sortable,
            tooltipField: x.tooltipField,
            hide: x.hide,
            cellStyle: x.cellStyle,
            maxWidth: x.maxWidth,
            minWidth: x.minWidth,
            width: x.width,
            cellRendererParams: x.cellRendererParams,
            cellRenderer: x.cellRenderer
        } as ColDef;
    });

    const callStrategyColumns = comp.callStrategies.map(strategy => {

        const call = {
            headerValueGetter: (args) => strategy.isDirty ? `${strategy.strategyName}` : strategy.strategyName,
            headerClass: (args) => strategy.isDirty ? 'opg-difference' : undefined,
            colId: `pricing-${strategy.strategyId}-call`,
            field: `pricing-${strategy.strategyId}-call.price`,
            tooltipField: `pricing-${strategy.strategyId}-call.tooltip`,
            sortable: false,
            valueFormatter: defaultPriceCellFormatterWithDollarSign,
            cellStyle: getPricingColumnCellStyle(comp),
            cellClassRules: {'opg-copilot-x': (args) => comp.isCopilotBorderCell(args)},
            valueGetter: getPricingRowValueGetter(comp, strategy, `pricing-${strategy.strategyId}-call`),
            minWidth: 100,
            suppressSizeToFit: true
        };

        return call as ColDef;

    });

    const putStrategyColumns = comp.putStrategies.map(strategy => {

        const put = {
            headerValueGetter: (args) => strategy.isDirty ? `${strategy.strategyName}` : strategy.strategyName,
            headerClass: (args) => strategy.isDirty ? 'opg-difference' : undefined,
            colId: `pricing-${strategy.strategyId}-put`,
            field: `pricing-${strategy.strategyId}-put.price`,
            tooltipField: `pricing-${strategy.strategyId}-put.tooltip`,
            sortable: false,
            valueFormatter: defaultPriceCellFormatterWithDollarSign,
            cellStyle: getPricingColumnCellStyle(comp),
            cellClassRules: {'opg-copilot-x': (args) => comp.isCopilotBorderCell(args)},
            valueGetter: getPricingRowValueGetter(comp, strategy, `pricing-${strategy.strategyId}-put`),
            minWidth: 100,
            suppressSizeToFit: true
        };
        return put as ColDef;
    });

    // insert call strategies
    existingCallChildren.splice(2, 0, ...callStrategyColumns.reverse());

    // insert put strategies
    existingPutChildren.splice(3, 0, ...putStrategyColumns);

    const cols: (ColDef | ColGroupDef)[] = [
        {
            headerName: 'CALLS',
            groupId: 'calls',
            children: existingCallChildren
        },

        ... getStrikeColumn(comp),

        {
            headerName: 'PUTS',
            groupId: 'puts',
            children: existingPutChildren
        }
    ];

    return cols;
}

function getPricingRowValueGetter(comp: OptionsPricingGridComponentBase, strategy: PricingGridStrategy, colId: string) {

    const f = function pricingRowValueGetter(args: ValueGetterParams): string {

        const row: PricingGridRow = args.data;

        if (!row) {
            return '';
        }

        if (!isValidNumber(comp.atmStrike, true)) {
            return '';
        }

        let sourceRow = row;
        if (comp.pricingMode) {
            const mirrorStrike = 2 * comp.atmStrike - row.rowStrike;
            const mirror = comp.pricingRows.find(r => r.rowStrike === mirrorStrike);
            if (!mirror) return '';
            sourceRow = mirror;
        }

        const col: PricingGridStrategyColumn = sourceRow[colId];

        if (!col) {
            return '';
        }


        const isCall = colId.endsWith('call');

        let isItm = true;

        if (isCall) {
            isItm = row.rowStrike < comp.atmStrike;
        } else {
            isItm = row.rowStrike > comp.atmStrike;
        }

        if (isItm && !comp.showItmData && !comp.pricingMode) {
            return '';
        }

        if (strategy.calendarized) {
            if (strategy.brokenExpiration) {
                return 'N/A';
            }
        }

        return col.price as any;
    };

    return f;
}

function getPricingColumnCellStyle(comp: OptionsPricingGridComponentBase) {

    const f = function pricingColumnCellStyle(args: CellClassParams) {

        const styleObj = {};

        styleObj['font-weight'] = 'normal';
        styleObj['background-color'] = 'rgba(0,0,0,0)';
        styleObj['color'] = 'inherit'


        if (!args.colDef.field) {
            return styleObj;
        }

        if (!args.colDef.field.startsWith('pricing')) {
            return styleObj;
        }

        if (comp.pricingMode) {
            if (isValidNumber(args.value, true)) {
                styleObj['color'] = args.value > 0 ? '#00c853' : args.value < 0 ? '#ff1744' : 'inherit';
            }
            return styleObj;
        }

        const objAttributeName = args.colDef.field.split('.')[0];

        const data = args.data as PricingGridRow;
        const pricingCol = data[objAttributeName] as PricingGridStrategyColumn;

        if (!pricingCol) {
            return styleObj;
        }

        if (pricingCol.optionType === OptionType.Call) {
            if (isNullOrUndefined(args.data.callOtm)) {
                if (!comp.showItmData) {
                    return styleObj;
                }
            }
        }

        if (pricingCol.optionType === OptionType.Put) {
            if (isNullOrUndefined(args.data.putOtm)) {
                if (!comp.showItmData) {
                    return styleObj;
                }
            }
        }

        if (pricingCol.rMultiple) {
            styleObj['font-weight'] = '800';
        } else {
            styleObj['font-weight'] = 'normal';
        }

        if (pricingCol.payForIt) {
            styleObj['color'] = 'yellow';
        } else {
            styleObj['color'] = 'inherit';
        }

        if (pricingCol.percentageProfit) {
            styleObj['background-color'] = 'green';
        } else {
            styleObj['background-color'] = 'rgba(0,0,0,0)';
        }


        if (pricingCol.highlightColor) {
            styleObj['background-color'] = pricingCol.highlightColor;
            styleObj['font-weight'] = 'bold';
        }

        if (args.value === 'N/A') {
            styleObj['color'] = 'red';
        } else {
            styleObj['color'] = 'inherit';
        }

        return styleObj;

    };

    return f;

}

function getStrikeColumn(comp: OptionsPricingGridComponentBase): any[] {

    const columns = [];

    const strikeColumn = {
        colId: 'strikeColumn',
        headerName: 'Strike',
        field: 'rowStrike',
        sort: 'desc',
        cellStyle(args: CellClassParams) {

            const marketAtm: number = comp.getCurrentAtm();
            const isMarketAtm = marketAtm === args.data.rowStrike;

            if (args.data.isAtm) {

                let background = 'black';

                if (isMarketAtm) {
                    background = 'linear-gradient(90deg, rgba(0, 0, 0, 1) 50%, rgba(255, 255, 255, 1) 0%)';
                }

                return {
                    background: background,
                    color: 'gold',
                    'font-weight': '800'
                };

            } else {
                let background = 'cornflowerblue';
                let color = 'white';

                const expMoveArgs: ExpectedMoveArgs = {
                    strike: args.data.rowStrike,
                    source: 'OPG',
                    ticker: comp.tradingInstrument?.ticker,
                    expiration: comp.expiration?.optionExpirationDate,
                    expirationTicker: comp.expiration?.expirationTicker,
                    underlyingTicker: comp.expiration?.underlyingTicker,
                    column: undefined
                };

                const rangeRegion = comp.expectedMoveService.getStrikeRegion(expMoveArgs);

                if (rangeRegion) {
                    background = rangeRegion === 'near' ? 'grey' : '#55565b';
                }

                if (isMarketAtm) {
                    background = 'white';
                    color = 'black';
                }

                return {
                    background: background,
                    color: color,
                    'font-weight': '800'
                };

            }
        },
        minWidth: 140
    };

    columns.push(strikeColumn);

    const pnlColumn = {
        colId: 'totalPnl-0',
        headerValueGetter: () => comp.getTotalPnlColumnHeader('totalPnl-0'),
        hide: !comp.showPnl2 || !comp.bothSidesHasSelected(),
        minWidth: minCellWidth + 10,
        valueGetter: params => {
            const row = params.node.data as PricingGridRow;
            const totalPnl = row.callPnl0 + row.putPnl0;
            return totalPnl;
        },
        valueFormatter: (args: ValueFormatterParams) => {
            if (!args.value) {
                return undefined;
            }
            return formatCurrency(args.value, 'en-US', '$', 'USD', '1.0-0');
        },
    } as ColDef;

    const pnlColumn1 = {
        colId: 'totalPnl-1',
        headerValueGetter: () => comp.getTotalPnlColumnHeader('totalPnl-1'),
        hide: !comp.showPnl2 || !comp.bothSidesHasSelected() || !comp.isAnyStrategyCalendarized(),
        minWidth: minCellWidth + 10,
        valueGetter: params => {
            const row = params.node.data as PricingGridRow;
            const totalPnl = row.callPnl1 + row.putPnl1;
            return totalPnl;
        },
        valueFormatter: (args: ValueFormatterParams) => {
            if (!args.value) {
                return undefined;
            }
            return formatCurrency(args.value, 'en-US', '$', 'USD', '1.0-0');
        },
    } as ColDef;

    columns.push(pnlColumn);
    columns.push(pnlColumn1);

    const strikeColumn2 = {
        colId: 'strikeColumn2',
        headerName: 'Strike',
        field: 'rowStrike',
        hide: !comp.showPnl2 || !comp.bothSidesHasSelected(),
        cellStyle(args: CellClassParams) {

            const marketAtm: number = comp.getCurrentAtm();
            const isMarketAtm = marketAtm === args.data.rowStrike;

            if (args.data.isAtm) {

                let background = 'black';

                if (isMarketAtm) {
                    background = 'linear-gradient(90deg, rgba(0, 0, 0, 1) 50%, rgba(255, 255, 255, 1) 0%)';
                }

                return {
                    background: background,
                    color: 'gold',
                    'font-weight': '800'
                };

            } else {
                let background = 'cornflowerblue';
                let color = 'white';

                const expMoveArgs: ExpectedMoveArgs = {
                    strike: args.data.rowStrike,
                    source: 'OPG',
                    ticker: comp.tradingInstrument?.ticker,
                    expiration: comp.expiration?.optionExpirationDate,
                    expirationTicker: comp.expiration?.expirationTicker,
                    underlyingTicker: comp.expiration?.underlyingTicker,
                    column: undefined
                };

                const rangeRegion = comp.expectedMoveService.getStrikeRegion(expMoveArgs);

                if (rangeRegion) {
                    background = rangeRegion === 'near' ? 'grey' : '#55565b';
                }

                if (isMarketAtm) {
                    background = 'white';
                    color = 'black';
                }

                return {
                    background: background,
                    color: color,
                    'font-weight': '800'
                };

            }
        },
        minWidth: 140,
    };

    columns.push(strikeColumn2);

    return [{
        groupId: 'strike',
        headerClass: 'expected-move-group-header',
        headerValueGetter: () => comp.expectedRangeText,
        children: columns,
    }];
}

function getEmptyGridTemplate(comp: OptionsPricingGridComponentBase): ColDef[] {

    const cols: ColDef[] = [
        {
            headerName: 'CALLS',
            groupId: 'calls',
            children: [
                {
                    headerName: '% OTM',
                    field: 'callOtm',
                    sortable: false,
                    valueFormatter: (args: ValueFormatterParams) => {
                        if (!args.value) {
                            return undefined;
                        }
                        return formatNumber(args.value * 100, 'en-US', '1.1-1') + '%';
                    },
                    maxWidth: minCellWidth,
                    minWidth: minCellWidth,
                } as ColDef,
                {
                    headerName: '$ OTM',
                    field: 'callOtmPts',
                    sortable: false,
                    maxWidth: minCellWidth,
                    minWidth: minCellWidth
                },
                {
                    headerName: 'IV',
                    field: 'callIV',
                    sortable: false,
                    hide: true,
                    valueFormatter: (args: ValueFormatterParams) => {

                        if (!args.value) {
                            return '';
                        }

                        if (!comp.showItmData) {
                            const data: PricingGridRow = args.data;
                            if (isNullOrUndefined(data.callOtm)) {
                                return '';
                            }
                        }

                        return formatNumber(args.value * 100, 'en-US', '1.1-1') + '%';
                    },
                },
                {
                    headerName: 'Delta',
                    field: 'callDelta',
                    sortable: false,
                    hide: true,
                    valueFormatter: defaultPriceCellFormatter
                },
                {
                    headerName: 'Bid',
                    field: 'callBid',
                    sortable: false,
                    hide: true,
                    valueFormatter: defaultPriceCellFormatter
                },
                {
                    headerName: 'Ask',
                    field: 'callAsk',
                    sortable: false,
                    hide: true,
                    valueFormatter: defaultPriceCellFormatter
                },
                {
                    headerName: 'Own',
                    headerClass: 'pmd-column',
                    field: 'callPrice',
                    colId: 'own-call',
                    maxWidth: maxCellWidth,
                    minWidth: maxCellWidth,
                    hide: comp.mode === 'opg',
                    cellRenderer: 'opgPriceCellRenderer',
                    cellRendererParams: {
                        component: comp,
                        optionType: 'Call',
                        pmdColumn: 'own'
                    },
                },
                {
                    headerName: 'Trans.',
                    headerClass: 'pmd-column',
                    field: 'callPrice',
                    colId: 'trans-call',
                    maxWidth: maxCellWidth,
                    minWidth: maxCellWidth,
                    hide: comp.mode === 'opg',
                    cellRenderer: 'opgPriceCellRenderer',
                    cellRendererParams: {
                        component: comp,
                        optionType: 'Call',
                        pmdColumn: 'trans'
                    },
                },
                {
                    headerName: comp.mode === 'opg' ? 'Price' : 'Outcome',
                    headerClass: comp.mode === 'pmd' ? 'pmd-column' : undefined,
                    field: 'callPrice',
                    colId: 'price-call',
                    sortable: false,
                    cellRenderer: 'opgPriceCellRenderer',
                    cellRendererParams: {
                        component: comp,
                        optionType: 'Call'
                    },
                    maxWidth: maxCellWidth,
                    minWidth: maxCellWidth
                },
                {
                    headerValueGetter: () => comp.getPnlColumnHeader('pnl-put-1'),
                    colId: 'pnl-put-1',
                    field: 'putPnl1',
                    sortable: false,
                    valueFormatter: (args: ValueFormatterParams) => {
                        if (!args.value) {
                            return undefined;
                        }
                        return formatNumber(args.value * 100, 'en-US', '1.1-1') + '%';
                    },
                    maxWidth: maxCellWidth,
                    minWidth: minCellWidth,
                    hide: true
                },
                {
                    headerValueGetter: () => comp.getPnlColumnHeader('pnl-put-0'),
                    colId: 'pnl-put-0',
                    field: 'putPnl0',
                    sortable: false,
                    valueFormatter: (args: ValueFormatterParams) => {
                        if (!args.value) {
                            return undefined;
                        }
                        return formatCurrency(args.value, 'en-US', '$', 'USD', '1.0-0');
                    },
                    maxWidth: maxCellWidth,
                    minWidth: minCellWidth,
                    hide: true
                },
            ],
        } as Partial<ColDef>,

        ...getStrikeColumn(comp),

        {
            headerName: 'PUTS',
            groupId: 'puts',
            children: [

                {
                    headerValueGetter: () => comp.getPnlColumnHeader('pnl-call-0'),
                    colId: 'pnl-call-0',
                    field: 'callPnl0',
                    sortable: false,
                    valueFormatter: (args: ValueFormatterParams) => {
                        if (!args.value) {
                            return undefined;
                        }
                        return formatCurrency(args.value, 'en-US', '$', 'USD', '1.0-0');
                    },
                    maxWidth: maxCellWidth,
                    minWidth: minCellWidth,
                    hide: true
                },
                {
                    headerValueGetter: () => comp.getPnlColumnHeader('pnl-call-1'),
                    colId: 'pnl-call-1',
                    field: 'callPnl1',
                    sortable: false,
                    valueFormatter: (args: ValueFormatterParams) => {
                        if (!args.value) {
                            return undefined;
                        }
                        return formatCurrency(args.value, 'en-US', '$', 'USD', '1.0-0');
                    },
                    maxWidth: maxCellWidth,
                    minWidth: minCellWidth,
                    hide: true
                },

                {
                    headerName: comp.mode === 'opg' ? 'Price' : 'Outcome',
                    headerClass: comp.mode === 'pmd' ? 'pmd-column' : undefined,
                    field: 'putPrice',
                    colId: 'price-put',
                    sortable: false,
                    cellRenderer: 'opgPriceCellRenderer',
                    cellRendererParams: {
                        component: comp,
                        optionType: 'Put'
                    },
                    maxWidth: maxCellWidth,
                    minWidth: maxCellWidth
                },
                {
                    headerName: 'Trans.',
                    headerClass: 'pmd-column',
                    field: 'putPrice',
                    colId: 'trans-put',
                    maxWidth: maxCellWidth,
                    minWidth: maxCellWidth,
                    hide: comp.mode === 'opg',
                    cellRenderer: 'opgPriceCellRenderer',
                    cellRendererParams: {
                        component: comp,
                        optionType: 'Put',
                        pmdColumn: 'trans'
                    },
                },
                {
                    headerName: 'Own',
                    headerClass: 'pmd-column',
                    field: 'putPrice',
                    colId: 'own-put',
                    maxWidth: maxCellWidth,
                    minWidth: maxCellWidth,
                    hide: comp.mode === 'opg',
                    cellRenderer: 'opgPriceCellRenderer',
                    cellRendererParams: {
                        component: comp,
                        optionType: 'Put',
                        pmdColumn: 'own'
                    },
                },
                {
                    headerName: 'Bid',
                    field: 'putBid',
                    sortable: false,
                    hide: true,
                    valueFormatter: defaultPriceCellFormatter
                },
                {
                    headerName: 'Ask',
                    field: 'putAsk',
                    sortable: false,
                    hide: true,
                    valueFormatter: defaultPriceCellFormatter
                },
                {
                    headerName: 'Delta',
                    field: 'putDelta',
                    sortable: false,
                    hide: true,
                    valueFormatter: defaultPriceCellFormatter
                },
                {
                    headerName: 'IV',
                    field: 'putIV',
                    sortable: false,
                    hide: true,
                    valueFormatter: (args: ValueFormatterParams) => {
                        if (!args.value) {
                            return '';
                        }

                        if (!comp.showItmData) {
                            const data: PricingGridRow = args.data;
                            if (isNullOrUndefined(data.putOtm)) {
                                return '';
                            }
                        }

                        return formatNumber(args.value * 100, 'en-US', '1.1-1') + '%';
                    },
                },
                {
                    headerName: '$ OTM',
                    field: 'putOtmPts',
                    sortable: false,
                    maxWidth: minCellWidth,
                    minWidth: minCellWidth
                },
                {
                    headerName: '% OTM',
                    field: 'putOtm',
                    sortable: false,
                    valueFormatter: (args: ValueFormatterParams) => {
                        if (!args.value) {
                            return undefined;
                        }
                        return formatNumber(args.value * 100, 'en-US', '1.1-1') + '%';
                    },
                    maxWidth: minCellWidth,
                    minWidth: minCellWidth
                }
            ]
        } as Partial<ColDef>,
    ];


    return cols;
}
